import { lazy } from 'react';

// vista#602: after a deploy, Vite's hashed chunk filenames change. A tab
// loaded before the deploy still references the old hash, so its next dynamic
// import 404s ("Failed to fetch dynamically imported module") and dead-ends on
// the ErrorBoundary. Recover by reloading once to fetch the fresh index +
// chunks. A short time-window stamp (sessionStorage) prevents a reload loop
// when the chunk is genuinely gone (then the ErrorBoundary surfaces it).

const RELOAD_KEY = 'vista:chunk-reload-at';
const RELOAD_WINDOW_MS = 10_000;

// #603 review: only the stale-chunk signature should trigger a reload — a
// genuinely-broken page module must surface to the ErrorBoundary directly, not
// get one spurious reload first. Match the dynamic-import failure messages
// browsers emit (Chrome/Firefox/Safari variants).
const CHUNK_ERROR_RE = /dynamically imported module|importing a module script failed|error loading dynamically imported/i;
export function isChunkLoadError(err) {
  const msg = (err && (err.message || `${err}`)) || '';
  return CHUNK_ERROR_RE.test(msg);
}

// #603 review: within a single page load, reload at most once even if several
// chunks fail together. (Cross-reload looping is guarded by the sessionStorage
// stamp; this just bounds the same-load case.)
let reloadAttempted = false;

// Decide how to handle a failed lazy-chunk import. Triggers a one-time reload
// and returns a never-settling promise (so the lazy boundary hangs until the
// navigation takes over); otherwise re-throws so the ErrorBoundary shows it.
// Deps are injectable for testing (no real reload / storage / clock / flag).
export function recoverFromChunkError(err, deps = {}) {
  const isChunk = deps.isChunkError ?? isChunkLoadError;
  // Not a stale-chunk error → surface immediately, no reload (#603).
  if (!isChunk(err)) throw err;

  const memGet = deps.memGet ?? (() => reloadAttempted);
  const memSet = deps.memSet ?? ((v) => { reloadAttempted = v; });
  // Already reloaded once this page load (e.g. multiple chunks failed) → surface.
  if (memGet()) throw err;

  const now = deps.now ?? Date.now();
  const reload = deps.reload ?? (() => window.location.reload());

  // Cross-reload guard via sessionStorage. #603 review: if storage is
  // unavailable we cannot prevent a reload loop, so we DON'T auto-reload —
  // surfacing to the ErrorBoundary is safer than looping.
  let last = 0;
  try {
    const raw = (deps.getStamp ?? (() => sessionStorage.getItem(RELOAD_KEY)))();
    last = Number(raw) || 0;
  } catch {
    throw err; // can't read the guard → don't reload
  }
  if (now - last <= RELOAD_WINDOW_MS) throw err; // reloaded recently → surface

  try {
    (deps.setStamp ?? ((v) => sessionStorage.setItem(RELOAD_KEY, v)))(String(now));
  } catch {
    throw err; // can't persist the guard → don't reload (avoid a loop)
  }
  memSet(true);
  reload();
  return new Promise(() => {}); // hang until the reload replaces the page
}

// Drop-in replacement for React.lazy that auto-recovers from a stale chunk.
export function lazyWithReload(factory) {
  return lazy(() => factory().catch((err) => recoverFromChunkError(err)));
}
